import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';

import {
  ShieldCheck,
  Users,
  User,
  Building2,
  Globe,
  TrendingUp,
  FileCheck2,
  Sparkles,
  Check,
  Workflow,
  CheckCircle2,
  QrCode,
  Lock,
  Eye,
  Clock,
  Share2,
  Zap,
  Crown,
} from 'lucide-react';
import { Menu, X } from 'lucide-react';
import { useState } from 'react';
/* =========================
   DESIGN SYSTEM
========================= */
const PRIMARY = '#375AE7';
const ACCENT = '#7C8CF8';
const SOFT = '#EEF2FF';
const BG = '#F6F8FC';

/* =========================
   DATA
========================= */
const kpis = [
  { label: 'Total Clients', value: '6,450+', icon: Users },
  { label: 'Team Members', value: '1,280', icon: Users },
  { label: 'Compliance Officers', value: '340', icon: ShieldCheck },
  { label: 'Agencies', value: '95', icon: Building2 },
];

const countries = [
  { country: 'AU', value: 38 },
  { country: 'UK', value: 22 },
  { country: 'UAE', value: 15 },
  { country: 'IN', value: 14 },
  { country: 'SG', value: 11 },
];

const growth = [
  { m: 'Jan', v: 4200 },
  { m: 'Feb', v: 5400 },
  { m: 'Mar', v: 6100 },
  { m: 'Apr', v: 7900 },
  { m: 'May', v: 9500 },
];

const steps = [
  {
    title: 'Onboard',
    desc: 'Quick digital onboarding with secure identity capture',
    icon: Users,
  },
  {
    title: 'Verify',
    desc: 'Automated KYC, KYB and AML checks instantly',
    icon: FileCheck2,
  },
  {
    title: 'Comply',
    desc: 'Audit-ready compliance records generated',
    icon: ShieldCheck,
  },
  {
    title: 'Monitor',
    desc: 'Real-time risk monitoring and alerts',
    icon: TrendingUp,
  },
];

const pricing = [
  {
    name: 'Boutique Agencies',
    price: '$95/month',
    desc: 'Up to 5 agents',
    highlight: false,
  },
  {
    name: 'Small–Medium Agencies',
    price: '$399/month',
    desc: '6–25 agents',
    highlight: false,
  },
  {
    name: 'Growing Agencies',
    price: '$699/month',
    desc: '26–50 agents',
    highlight: false,
  },
  {
    name: 'Enterprise Subscription',
    price: 'Custom Pricing',
    desc: '50+ agents',
    highlight: false,
  },
];
const data = [
  { name: 'Corporate', value: 3420 },
  { name: 'Individual', value: 5180 },
  { name: 'Trust', value: 1180 },
];

function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-md border-b">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* LOGO */}
        <div className="flex items-center gap-3">
          <div className="">
            {/* <ShieldCheck size={18} /> */}
            <img
              src="/Ratified-Logo-Main.svg"
              alt="Company Logo"
              className={`w-40 top-20 block`}
            />
          </div>
        </div>

        {/* DESKTOP MENU */}
        <div className="hidden md:flex items-center gap-3">
          {/* LOGIN */}
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 text-sm rounded-xl border hover:bg-slate-50 transition"
          >
            Login
          </button>

          {/* SIGNUP */}
          <button
            onClick={() => navigate('/sign-up')}
            className="px-5 py-2 text-sm rounded-xl text-white shadow-md hover:shadow-lg transition"
            style={{
              background: 'linear-gradient(135deg, #375AE7, #7C8CF8)',
            }}
          >
            Sign Up
          </button>
        </div>

        {/* MOBILE BUTTON */}
        <button
          className="md:hidden p-2 rounded-lg border"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* MOBILE MENU */}
      {open && (
        <div className="md:hidden px-6 pb-4 space-y-3 bg-white border-t">
          <button className="w-full py-2 rounded-lg border">Login</button>

          <button
            className="w-full py-2 rounded-lg text-white"
            style={{
              background: 'linear-gradient(135deg, #375AE7, #7C8CF8)',
            }}
          >
            Sign Up
          </button>
        </div>
      )}
    </header>
  );
}
export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: BG }}>
      <Header />

      {/* ================= HERO ================= */}
      <section className="max-w-7xl mx-auto px-6 py-6 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm mb-4"
            style={{ backgroundColor: SOFT, color: PRIMARY }}
          >
            <Sparkles className="w-4 h-4" />
            AML Made Easy
          </div>

          <h1 className="text-5xl font-bold leading-tight">
            AML compliance <span style={{ color: PRIMARY }}>made easy</span>
          </h1>

          <p className="mt-4 text-slate-600 text-lg">
            Automate identity verification, reduce risk, and stay compliant
            globally, without complexity.
          </p>
        </div>

        {/* Growth Chart */}
        <div className="bg-white p-6 rounded-3xl border shadow-sm">
          <h3 className="font-semibold mb-4">Overall Growth</h3>

          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={growth}>
              <XAxis dataKey="m" />
              <Tooltip />
              <Area dataKey="v" stroke={PRIMARY} fill={SOFT} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* ================= KPI ================= */}
      <section className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-6">
        {kpis.map((k, i) => (
          <div key={i} className="bg-white p-6 rounded-2xl border shadow-sm">
            <k.icon style={{ color: PRIMARY }} />
            <h2 className="text-2xl font-bold mt-3">{k.value}</h2>
            <p className="text-sm text-slate-500">{k.label}</p>
          </div>
        ))}
      </section>

      {/* ================= AML MADE EASY ================= */}
      {/* ================= AML MADE EASY (NEW PREMIUM) ================= */}
      <section className="max-w-7xl mx-auto px-6 py-6 grid md:grid-cols-2 gap-12 items-center">
        {/* LEFT SIDE */}
        <div>
          <h2 className="text-4xl font-bold leading-tight">
            Simplify compliance with{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #375AE7, #7C8CF8)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              automated intelligence
            </span>
          </h2>

          <p className="text-slate-600 mt-4 text-lg">
            Eliminate manual AML processes and streamline onboarding,
            verification, and monitoring — all in one platform.
          </p>

          {/* FEATURE LIST */}
          <div className="mt-8 space-y-4">
            {[
              'Real-time identity verification',
              'Automated AML & risk scoring',
              'Audit-ready compliance records',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div
                  className="p-2 rounded-lg"
                  style={{
                    background: 'linear-gradient(135deg, #375AE7, #7C8CF8)',
                    color: 'white',
                  }}
                >
                  ✓
                </div>
                <p className="text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT SIDE (INTERACTIVE CARDS) */}
        {/* ================= INSTANT VERIFICATION (PREMIUM CARD) ================= */}
        <div className="p-6 rounded-3xl border bg-white shadow-sm transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
          {/* HEADER */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-lg flex items-center gap-3">
              <span
                className="p-2 rounded-xl shadow-md"
                style={{
                  background: 'linear-gradient(135deg, #375AE7, #7C8CF8)',
                  color: 'white',
                }}
              >
                <ShieldCheck size={18} />
              </span>
              Instant Verification
            </h3>
          </div>

          {/* DESCRIPTION */}
          <p className="text-sm text-slate-500 mb-6">
            Real-time AML verification insights across all application types.
          </p>

          {/* METRIC CARDS */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {/* CORPORATE */}
            <div className="p-4 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100">
              <div className="flex items-center justify-between mb-2">
                <Building2 size={16} className="text-[#375AE7]" />
                <span className="text-xs text-slate-400">+12%</span>
              </div>
              <p className="text-xs text-slate-500">Corporate</p>
              <h4 className="text-xl font-bold text-[#375AE7]">3,420</h4>
            </div>

            {/* INDIVIDUAL */}
            <div className="p-4 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-200">
              <div className="flex items-center justify-between mb-2">
                <User size={16} className="text-slate-600" />
                <span className="text-xs text-slate-400">+18%</span>
              </div>
              <p className="text-xs text-slate-500">Individual</p>
              <h4 className="text-xl font-bold text-slate-700">5,180</h4>
            </div>

            {/* AGENTS */}
            <div className="p-4 rounded-2xl bg-gradient-to-br from-indigo-50 to-blue-100">
              <div className="flex items-center justify-between mb-2">
                <ShieldCheck size={16} className="text-indigo-500" />
                <span className="text-xs text-slate-400">+9%</span>
              </div>
              <p className="text-xs text-slate-500">Trust</p>
              <h4 className="text-xl font-bold text-indigo-600">1,180</h4>
            </div>
          </div>

          {/* CHART */}
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#375AE7" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section className="max-w-7xl mx-auto px-6 pb-5">
        <h2 className="text-2xl font-bold mb-8">How it works</h2>

        <div className="grid md:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="bg-white p-6 rounded-2xl border shadow-sm">
              <s.icon style={{ color: PRIMARY }} />
              <h3 className="font-semibold mt-3">{s.title}</h3>
              <p className="text-sm text-slate-500 mt-1">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ================= GLOBAL + STATUS ================= */}
      <section className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-6">
        {/* Countries */}
        <div className="bg-white p-6 rounded-3xl border">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Globe style={{ color: PRIMARY }} />
            Countries Coverage
          </h3>

          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={countries}>
              <XAxis dataKey="country" />
              <Tooltip />
              <Bar dataKey="value" fill={PRIMARY} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Certificate details */}
        <div className="bg-white p-6 rounded-3xl border shadow-sm transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
          {/* HEADER */}
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-3">
              <div
                className="p-3 rounded-2xl shadow-md"
                style={{
                  background: 'linear-gradient(135deg, #375AE7, #7C8CF8)',
                  color: 'white',
                }}
              >
                <ShieldCheck size={20} />
              </div>

              <div>
                <h3 className="text-lg font-semibold">
                  Verification Certificate Vault
                </h3>
                <p className="text-xs text-slate-500">
                  Verify once. Reuse securely when needed.
                </p>
              </div>
            </div>

            {/* STATUS BADGE */}
            <span className="text-xs px-3 py-1 rounded-full bg-green-50 text-green-600 font-medium">
              Active Secure Vault
            </span>
          </div>

          {/* MAIN DESCRIPTION */}
          <p className="text-sm text-slate-600 mb-6">
            Ratified generates a secure, reusable verification certificate that
            reduces repeated onboarding across providers while maintaining full
            compliance control.
          </p>

          {/* FEATURE GRID */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {[
              {
                icon: FileCheck2,
                title: 'Less Document Repetition',
                desc: 'Reuse verified identity across multiple services',
              },
              {
                icon: QrCode,
                title: 'Secure QR Sharing',
                desc: 'Share verification via encrypted QR or link',
              },
              {
                icon: Lock,
                title: 'Controlled Access',
                desc: 'View-only, time-limited, consent-based sharing',
              },
              {
                icon: Eye,
                title: 'Full Audit Trail',
                desc: 'Every access is logged and traceable',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="p-4 rounded-2xl border bg-gradient-to-br from-slate-50 to-white hover:shadow-md transition"
              >
                <div className="flex items-center gap-2 mb-2">
                  <item.icon size={16} style={{ color: '#375AE7' }} />
                  <h4 className="font-medium text-sm">{item.title}</h4>
                </div>
                <p className="text-xs text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* VAULT INSIGHT STRIP */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="p-3 rounded-xl bg-blue-50 text-center">
              <p className="text-lg font-bold text-[#375AE7]">98%</p>
              <p className="text-[10px] text-slate-500">Reuse Rate</p>
            </div>

            <div className="p-3 rounded-xl bg-indigo-50 text-center">
              <p className="text-lg font-bold text-indigo-600">2.4s</p>
              <p className="text-[10px] text-slate-500">Avg Access Time</p>
            </div>

            <div className="p-3 rounded-xl bg-slate-50 text-center">
              <p className="text-lg font-bold text-slate-700">100%</p>
              <p className="text-[10px] text-slate-500">Audit Logged</p>
            </div>
          </div>

          {/* SECURITY FOOTER */}
          <div className="flex items-center justify-between text-xs text-slate-500 border-t pt-4">
            <div className="flex items-center gap-2">
              <Clock size={14} />
              Time-limited access
            </div>

            <div className="flex items-center gap-2">
              <Share2 size={14} />
              Consent-based sharing
            </div>

            <div className="flex items-center gap-2">
              <Lock size={14} />
              View-only by default
            </div>
          </div>
        </div>
      </section>

      {/* ================= PRICING ================= */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        {/* HEADER */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold">Simple, transparent pricing</h2>
          <p className="text-slate-500 mt-2">
            Choose the plan that fits your compliance needs
          </p>
        </div>

        {/* GRID */}
        <div className="grid md:grid-cols-4 gap-8">
          {pricing.map((p, i) => (
            <div
              key={i}
              className={`relative p-7 rounded-3xl border transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl ${
                p.highlight ? 'scale-105 shadow-xl' : 'bg-white'
              }`}
              style={{
                background: p.highlight
                  ? 'linear-gradient(135deg, #375AE7, #7C8CF8)'
                  : 'white',
                color: p.highlight ? 'white' : '#0f172a',
                borderColor: p.highlight ? 'transparent' : '#E2E8F0',
              }}
            >
              {/* BADGE */}
              {p.highlight && (
                <div className="absolute -top-3 left-6 bg-white text-[#375AE7] text-xs px-3 py-1 rounded-full shadow">
                  Most Popular
                </div>
              )}

              {/* ICON */}
              <div
                className="w-12 h-12 flex items-center justify-center rounded-2xl mb-4"
                style={{
                  background: p.highlight
                    ? 'rgba(255,255,255,0.2)'
                    : 'linear-gradient(135deg, #EEF2FF, #E0E7FF)',
                }}
              >
                {i === 0 && <ShieldCheck size={20} />}
                {i === 1 && <Sparkles size={20} />}
                {i === 2 && <Zap size={20} />}
                {i === 3 && <Crown size={20} />}
              </div>

              {/* TITLE */}
              <h3 className="text-xl font-semibold">{p.name}</h3>

              {/* PRICE */}
              <p className="text-3xl font-bold mt-2">{p.price}</p>

              {/* DESCRIPTION */}
              <p
                className={`text-sm mt-2 ${p.highlight ? 'opacity-90' : 'text-slate-500'}`}
              >
                {p.desc}
              </p>
              {/* BUTTON */}
              <button
                onClick={() => navigate('/sign-up')}
                className="mt-6 w-full py-3 rounded-xl font-medium transition"
                style={{
                  backgroundColor: p.highlight ? 'white' : PRIMARY,
                  color: p.highlight ? PRIMARY : 'white',
                }}
              >
                Get Started
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <div className="max-w-4xl mx-auto mt-10 pb-10 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Ratified Pty Ltd
      </div>
    </div>
  );
}
